package eu.tintera.time.format

import eu.tintera.locale.AppLocale
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.TimeZone
import kotlinx.datetime.daysUntil
import kotlinx.datetime.monthsUntil
import kotlinx.datetime.toLocalDateTime
import kotlinx.datetime.yearsUntil
import kotlin.math.absoluteValue
import kotlin.math.roundToInt
import kotlin.time.DurationUnit
import kotlin.time.Instant

/**
 * Holds target and reference date-time values used during relative date-time formatting.
 *
 * @property target The target date-time to represent.
 * @property now The reference "now" date-time.
 */
data class RelativeValues(
    val target: LocalDateTime,
    val now: LocalDateTime
) {
    /** True if [target] is before [now]. */
    val isPast: Boolean get() = target < now
    /** True if [target] is after [now]. */
    val isFuture: Boolean get() = target > now
}

internal fun platformRelativeTimeFormat(
    target: Instant,
    now: Instant,
    timeZone: TimeZone,
    format: RelativeDateTimeFormat,
    locale: AppLocale,
): String {

    val scope = RelativeDateTimeFormatScope(
        value = RelativeValues(
            target = target.toLocalDateTime(timeZone),
            now = now.toLocalDateTime(timeZone),
        ),
        locale = locale,
        timeZone = timeZone,
    )

    format.block(scope)

    if (scope.years == null && scope.months == null && scope.days == null && scope.hours == null && scope.minutes == null && scope.seconds == null) {
        throw EmptyFormatConfigurationException("RelativeDateTimeFormat cannot be empty.")
    }

    val measurable = computeRelativeUnit(
        target = target,
        now = now,
        zone = timeZone,
        scope = scope,
    )

    return nativeRelativeTimeFormat(
        measurable = measurable,
        style = scope.style,
        locale = locale,
        display = scope.display,
    )
}

internal expect fun nativeRelativeTimeFormat(
    measurable: Measurable?,
    style: FormatStyle,
    display: RelativeDisplay,
    locale: AppLocale
): String

internal fun computeRelativeUnit(
    target: Instant,
    now: Instant,
    zone: TimeZone,
    scope: RelativeDateTimeFormatScope
): Measurable? {

    scope.years?.let { threshold ->
        val years = now.yearsUntil(target, zone)
        if (years.absoluteValue >= threshold.min) return Measurable(MeasureUnit.YEARS, years)
    }

    scope.months?.let { threshold ->
        val months = now.monthsUntil(target, zone)
        if (months.absoluteValue >= threshold.min) return Measurable(MeasureUnit.MONTHS, months)
    }

    scope.days?.let { threshold ->
        // Days are counted as the difference of calendar dates, not as elapsed 24-hour periods: "yesterday" and
        // "2 days ago" name a calendar day, so 23 Oct 15:00 seen on 26 Oct 10:00 is "3 days ago", not "2 days ago".
        // Under one full day of elapsed time the smaller units still take over, as before.
        val elapsedDays = now.daysUntil(target, zone)
        val days = now.toLocalDateTime(zone).date.daysUntil(target.toLocalDateTime(zone).date)
        if (elapsedDays != 0 && days.absoluteValue >= threshold.min) return Measurable(MeasureUnit.DAYS, days)
    }

    val duration = target - now

    scope.hours?.let { threshold ->
        val rawHours = duration.toDouble(DurationUnit.HOURS)
        val hours = when (threshold.roundingMode) {
            RelativeRoundingMode.Truncate -> rawHours.toInt()
            RelativeRoundingMode.Round -> rawHours.roundToInt()
        }
        if (hours.absoluteValue >= threshold.min) return Measurable(MeasureUnit.HOURS, hours)
    }

    scope.minutes?.let { threshold ->
        val rawMinutes = duration.toDouble(DurationUnit.MINUTES)
        val minutes = when (threshold.roundingMode) {
            RelativeRoundingMode.Truncate -> rawMinutes.toInt()
            RelativeRoundingMode.Round -> rawMinutes.roundToInt()
        }
        if (minutes.absoluteValue >= threshold.min) return Measurable(MeasureUnit.MINUTES, minutes)
    }

    scope.seconds?.let { threshold ->
        val rawSeconds = duration.toDouble(DurationUnit.SECONDS)
        val seconds = when (threshold.roundingMode) {
            RelativeRoundingMode.Truncate -> rawSeconds.toInt()
            RelativeRoundingMode.Round -> rawSeconds.roundToInt()
        }
        if (seconds.absoluteValue >= threshold.min) return Measurable(MeasureUnit.SECONDS, seconds)
    }

    return null
}
